import React, { useState } from "react";
import { Link2, ExternalLink, Plus, X, AlertTriangle, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

function daysUntil(dateStr) {
    if (!dateStr) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const target = new Date(dateStr + "T00:00:00");
    return Math.ceil((target - today) / 86400000);
}

export default function AdditionalLinksPanel({ requestId, links = [], onChange, currentUser }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ url: "", label: "", expires_at: "" });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.url.trim()) return toast.error("Vul een link in");
        setSaving(true);
        try {
            await api.post(`/requests/${requestId}/additional-links`, {
                url: form.url.trim(),
                label: form.label.trim() || null,
                expires_at: form.expires_at || null,
            });
            toast.success("Link toegevoegd");
            setForm({ url: "", label: "", expires_at: "" });
            setOpen(false);
            onChange && onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSaving(false);
    };

    const remove = async (linkId) => {
        try {
            await api.delete(`/requests/${requestId}/additional-links/${linkId}`);
            onChange && onChange();
        } catch {}
    };

    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="additional-links-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-[#E5A93D]" />
                    <h2 className="font-display font-bold text-xl tracking-tight">Extra materiaal (links)</h2>
                </div>
                <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-sm text-[#E5A93D] hover:text-[#C8922C]" data-testid="toggle-add-link">
                    <Plus className="h-4 w-4" /> {open ? "Sluiten" : "Link toevoegen"}
                </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
                Gebruik dit voor extra foto's of materiaal via WeTransfer, Dropbox, Google Drive, ... Voeg de vervaldatum toe zodat we weten wanneer de link verloopt.
            </p>

            {open && (
                <div className="mb-4 p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm space-y-3" data-testid="add-link-form">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">URL *</Label>
                        <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://wetransfer.com/..." className="bg-[#FFFFFF] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-10" data-testid="link-url-input" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Label (optioneel)</Label>
                            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="bv. Extra foto's tuin" className="bg-[#FFFFFF] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-10" data-testid="link-label-input" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Vervaldatum link</Label>
                            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="bg-[#FFFFFF] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-10" data-testid="link-expires-input" />
                        </div>
                    </div>
                    <Button onClick={submit} disabled={saving} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="submit-link-btn">
                        {saving ? "Toevoegen..." : "Link toevoegen"}
                    </Button>
                </div>
            )}

            {links.length === 0 ? (
                <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">Nog geen extra links toegevoegd</div>
            ) : (
                <div className="space-y-2">
                    {links.map((l) => {
                        const days = daysUntil(l.expires_at);
                        const expired = days !== null && days < 0;
                        const expiringSoon = days !== null && days >= 0 && days <= 3;
                        const canRemove = currentUser && (currentUser.role === "admin" || currentUser.id === l.added_by_id);
                        return (
                            <div key={l.id} className={`flex items-center gap-3 p-3 rounded-sm border ${expired ? "bg-red-500/10 border-red-500/40" : expiringSoon ? "bg-yellow-500/10 border-yellow-500/40" : "bg-[#F4F4F5] border-zinc-200"}`} data-testid={`link-item-${l.id}`}>
                                <Link2 className={`h-4 w-4 flex-shrink-0 ${expired ? "text-red-600" : "text-[#E5A93D]"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-950 hover:text-[#E5A93D] truncate font-medium">
                                            {l.label || l.url}
                                        </a>
                                        {expired && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-red-500 text-zinc-950 px-1.5 py-0.5 rounded-sm font-bold"><AlertTriangle className="h-3 w-3" />Vervallen</span>}
                                        {expiringSoon && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-yellow-500 text-black px-1.5 py-0.5 rounded-sm font-bold"><Clock className="h-3 w-3" />Verloopt {days === 0 ? "vandaag" : `over ${days}d`}</span>}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {l.added_by_name} · {new Date(l.added_at).toLocaleDateString("nl-BE")}
                                        {l.expires_at && !expired && !expiringSoon && <> · verloopt op {l.expires_at}</>}
                                    </div>
                                </div>
                                <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[#E5A93D]" data-testid={`open-link-${l.id}`}>
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                                {canRemove && (
                                    <button onClick={() => remove(l.id)} className="text-zinc-500 hover:text-red-600" data-testid={`remove-link-${l.id}`}>
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
